// Pure helpers shared by the Live Entry page + its sub-components.
// Keep these stateless so they can be unit-tested in isolation.

// Placement dropdown values. Short-form labels (1st / 2nd / 3rd / HC / C)
// match the codes on the printed scoresheet, the Portal print, and the
// results PDF legend — and stay narrow enough for the Live Entry grid
// to render on phone / iPad without stealing space from Image Title.
export const PLACEMENTS = [
  { value: "NONE", label: "—" },
  { value: "FIRST", label: "1st" },
  { value: "SECOND", label: "2nd" },
  { value: "THIRD", label: "3rd" },
  { value: "HIGHLY_COMMENDED", label: "HC" },
  { value: "COMMENDED", label: "C" },
];

export const placementShortcut = (k) => {
  const map = { "1": "FIRST", "2": "SECOND", "3": "THIRD", h: "HIGHLY_COMMENDED", c: "COMMENDED", n: "NONE", "0": "NONE" };
  return map[k.toLowerCase()] || null;
};

export function sortByName(a, b) {
  const al = (a.last_name || "").toLowerCase();
  const bl = (b.last_name || "").toLowerCase();
  if (al !== bl) return al < bl ? -1 : 1;
  const af = (a.first_name || "").toLowerCase();
  const bf = (b.first_name || "").toLowerCase();
  return af < bf ? -1 : af > bf ? 1 : 0;
}

// Numeric-aware comparator so codes like "2", "3", "10", "A1" sort naturally
// rather than lexically (where "10" would land before "2").
const codeCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function sortByCode(a, b) {
  return codeCollator.compare(a.photographer_code || "", b.photographer_code || "");
}

export function sortByPhotographerName(rowA, rowB, photogs) {
  const a = photogs.find((p) => p.id === rowA.photographer_id);
  const b = photogs.find((p) => p.id === rowB.photographer_id);
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return sortByName(a, b);
}

// Sort live-entry rows by their photographer's I.D. (photographer_code),
// resolving from the photographers list when the row's own code is blank
// (e.g. fresh placeholders mid-save).
export function sortByPhotographerCode(rowA, rowB, photogs) {
  const codeA =
    rowA.photographer_code ||
    photogs.find((p) => p.id === rowA.photographer_id)?.photographer_code ||
    "";
  const codeB =
    rowB.photographer_code ||
    photogs.find((p) => p.id === rowB.photographer_id)?.photographer_code ||
    "";
  return codeCollator.compare(codeA, codeB);
}

function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Shuffle a list of rows so that:
//   • duplicates of the same photographer never sit in the same "round"
//     (when there are K copies of someone, each copy lands in its own round,
//      and the rows split into K consecutive blocks of equal size);
//   • each round is independently random;
//   • the round directly above is unlikely to begin with the same photographer
//     the previous round ended on;
//   • the resulting order is different from the input.
export function spreadShuffle(rows) {
  const n = rows.length;
  if (n < 2) return rows.slice();

  const keyOf = (r) => r.photographer_id || r.photographer_code || r._key;

  const groups = new Map();
  rows.forEach((r) => {
    const k = keyOf(r);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  });

  const K = Math.max(...[...groups.values()].map((g) => g.length));

  if (K === 1) {
    return shuffleInPlace(rows.slice());
  }

  const rounds = Array.from({ length: K }, () => []);
  for (const entries of groups.values()) {
    const slots = Array.from({ length: K }, (_, i) => i);
    shuffleInPlace(slots);
    entries.forEach((entry, i) => {
      rounds[slots[i]].push(entry);
    });
  }

  rounds.forEach((r) => shuffleInPlace(r));

  for (let i = 0; i < K - 1; i++) {
    const cur = rounds[i];
    const nxt = rounds[i + 1];
    if (!cur.length || !nxt.length) continue;
    if (keyOf(cur[cur.length - 1]) === keyOf(nxt[0]) && nxt.length > 1) {
      [nxt[0], nxt[1]] = [nxt[1], nxt[0]];
    }
  }

  let result = rounds.flat();

  const unchanged = result.every((r, i) => r._key === rows[i]._key);
  if (unchanged && n >= 2) {
    [result[0], result[n - 1]] = [result[n - 1], result[0]];
  }

  return result;
}

export function blankRow() {
  return {
    _key: Math.random().toString(36).slice(2),
    id: null,
    photographer_id: "",
    photographer_code: "",
    photographer_name: "",
    image_title: "",
    score: "",
    placement: "NONE",
    status: "empty", // empty | dirty | saving | saved | error
    error: "",
    duplicateWarn: false,
  };
}

// Translate the backend's technical "skipped" reasons into language anyone
// can understand at a glance.
export function friendlyReason(reason) {
  if (!reason) return "Couldn't read this row.";
  const r = reason.toLowerCase();
  if (r.includes("missing image title")) return "No image title in this row.";
  if (r.includes("missing score")) return "No score in this row.";
  if (r.includes("invalid score")) return "The score isn't a number.";
  if (r.includes("out of range")) return reason.replace(/score (\S+) out of range (\S+)-(\S+)/, "Score $1 is outside the allowed $2 – $3 range.");
  if (r.includes("negative score")) return "Score is less than zero — not allowed.";
  if (r.startsWith("ignored line")) return "We couldn't make sense of this line.";
  return reason;
}
